import { LitElement, html, css, nothing } from "lit";
import { virtualize } from '@lit-labs/virtualizer/virtualize.js';
import { property, state } from "lit/decorators.js";
import type { OutDesignAST } from "./types";
import { areOriginalStylesEnabled } from "../ui/shared";

import "./BlockRenderer";
import { Rule, applyRules } from "../rules";

export class AstRenderer extends LitElement {
  @property({ attribute: false })
  @state() private originalAst!: OutDesignAST;

  @state() public ast!: OutDesignAST;
  @state() private rules: Rule[];
  
  @state() private hasShadow: boolean = true ;

  private handleRulesChanged = (e: Event) => {
    const ev = e as CustomEvent<Rule[]>;
    
    this.rules = ev.detail;
    
    if (this.originalAst) {
      this.applyRules();
    }
  };
  
  static styles = css`
  :host {
    display: block;
    height: 100%;
    overflow-y: hidden !important;
    margin: 0px auto;
    width: 100%;
  }
  
  @media (min-width: 1250px) {
    :host {
      padding: 0 1em;
      min-width: 30em;
      max-width: 50em;
    }

    .shadow {
      box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    }
  }
  
  .virtualizer {
    height: 100%;
    display: block;
    caret-color: transparent;
    background-color: white;
  }

  block-renderer:first-child {
    padding-top: 1rem;
  }
  `;
  
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("rules-changed", this.handleRulesChanged);
    document.addEventListener("toggle-styles-view", this.toggleShadow);
    this.requestRules();
  }

  disconnectedCallback() {
    document.removeEventListener("rules-changed", this.handleRulesChanged);
    document.removeEventListener("toggle-styles-view", this.toggleShadow);
    super.disconnectedCallback();
  }

  private requestRules() {
    this.dispatchEvent(
      new CustomEvent("rules-request", {
        detail: { changes: true },
        bubbles: true,
        composed: true
      })
    );
  }

  private toggleShadow= () => {
    this.hasShadow = areOriginalStylesEnabled();
  };

  protected applyRules() {
    // console.log("Before applying rules:", this.ast)
    // console.log("Rules:", this.rules)
    if (!this.originalAst || !this.rules) return;
    this.ast = applyRules(this.originalAst, this.rules);
    // console.log("After applying rules:", this.ast)
  }
  
  render() {
    if (!this.ast) return html``;

    // FIXME: new adoptedStyleSheet do not seem to be acknowledged
    // when the view-css button is toggled...
    // FIXME: Same problem with the selections, they are not kept.
    return html`
    <div class="virtualizer ${this.hasShadow ? "shadow" : ""}">
      ${virtualize({
        scroller: true,
        items: this.ast.blocks,
        renderItem: block => html`<block-renderer .blocks=${[block]}></ast-renderer>`
      })}
      </div>
    `;
    }
}

customElements.define("ast-renderer", AstRenderer);